import React, {useEffect, useRef, useState} from 'react';
import {ElementTypeDto} from "../../../../../common/apis/Elements";
import {ReferenceDefinitionDto, ReferenceElementFilterDto} from "../../../../../common/apis/stereotype/ReferenceDefinitionDto";
import {StereotypeApiClient} from "../../../../../common/apis/stereotype/StereotypeApiClient";
import {StereotypeDto} from "../../../../../common/apis/stereotype/StereotypeDto";
import {ValidationError} from "../../../../../common/ValidationError";
import {Button} from "../../../../../components/button/Button";
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../../components/button/CancelButton";
import Dialog from '../../../../../components/dialogs/Dialog';
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from '../../../../../components/dialogs/DialogContent';
import DialogTitle from '../../../../../components/dialogs/DialogTitle';
import Grid from '../../../../../components/dialogs/Grid';
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import {_transl, t} from '../../../../../store/localization/TranslMessasge';
import {CommonTranslationKey} from '../../CommonTranslationKey';
import {useFindAllElementTypesQuery} from "../../elements/ElementQuery";
import {ElementTranslationKey} from "../../elements/ElementTranslationKey";
import ElementTypeSingleSelectField from "../../elements/ElementTypeSingleSelectField";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {StereotypeErrorCode} from "../StereotypeErrorCodes";
import {useFindAllStereotypesQuery} from "../StereotypeQuery";
import {StereotypesTranslationKey} from "../StereotypesTranslationKey";

interface RelatedElementFilterUpdateDialogProps {
    open: boolean;
    stereotypeId: string;
    referenceDefinition: ReferenceDefinitionDto;
    onUpdated: () => void;
    onClose: () => void;
}

export function RelatedElementFilterUpdateDialog(props: Readonly<RelatedElementFilterUpdateDialogProps>) {
    const {open, stereotypeId, referenceDefinition, onUpdated, onClose} = props;
    const stereotypeApiClient = useRef(new StereotypeApiClient());

    const [elementType, setElementType] = useState<ElementTypeDto | null>(null);
    const [stereotype, setStereotype] = useState<StereotypeDto | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    const {data: elementTypes = []} = useFindAllElementTypesQuery();
    const {data: stereotypes} = useFindAllStereotypesQuery();

    useEffect(() => {
        if (open && referenceDefinition) {
            const currentElementType = elementTypes.find(et => et.name === referenceDefinition.relatedElementFilter.type);
            setElementType(currentElementType ?? null);
            
            if (referenceDefinition.relatedElementFilter.stereotypeId && stereotypes && currentElementType) {
                const currentStereotype = stereotypes.find(s => s.id === referenceDefinition.relatedElementFilter.stereotypeId && s.elementType === currentElementType.name);
                setStereotype(currentStereotype ?? null);
            } else {
                setStereotype(null);
            }
            
            setErrors({});
        }
    }, [open, referenceDefinition, elementTypes, stereotypes]);

    function updateElementType(type: ElementTypeDto | null) {
        setElementType(type);
        setStereotype(null);
    }

    function updateRelatedElementFilter() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const elementFilter: ReferenceElementFilterDto = {
            type: elementType!.name,
            stereotypeId: stereotype?.id ?? null
        };

        stereotypeApiClient.current.updateReferenceElementFilter(stereotypeId, referenceDefinition.id, elementFilter)
                .then(() => {
                    onUpdated();
                    onClose();
                    Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                })
                .catch((error) => {
                    if (error instanceof ValidationError && error.error.code === StereotypeErrorCode.CONFLICTING_NAME) {
                        setErrors({elementType: t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED)});
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
                    }
                })
                .finally(() => setInProgress(false));
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        
        if (!elementType) {
            newErrors.elementType = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const stereotypeOptions: StereotypeDto[] = [];
    if (elementType && stereotypes) {
        stereotypeOptions.push(...stereotypes.filter(stereotype => stereotype.elementType === elementType.name));
    }

    return (
            <>
                <Dialog open={open} onClose={onClose} maxWidth="sm">
                    <DialogTitle title={t(StereotypesTranslationKey.EDIT_ELEMENT_FILTER)}
                            onDialogClosed={onClose} />
                    <DialogContent>
                        <Grid container>
                            <Grid item xs={12}>
                                <ElementTypeSingleSelectField
                                        label={_transl(ElementTranslationKey.DIALOG_CREATE_ELEMENT_FIELD_TYPE)}
                                        options={elementTypes}
                                        value={elementType}
                                        onChange={updateElementType}
                                        errorMessage={errors.elementType}/>
                            </Grid>
                            <Grid item xs={12}>
                                <SingleSelectField
                                        id={"stereotype"}
                                        label={_transl(StereotypesTranslationKey.STEREOTYPE)}
                                        value={stereotype}
                                        onChange={setStereotype}
                                        options={stereotypeOptions}
                                        getOptionLabel={(stereotype) => stereotype.name}
                                        disabled={stereotypeOptions.length === 0}/>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <ButtonLayout>
                            <PositiveButtons>
                                <Button label={t(CommonTranslationKey.SAVE)}
                                        variant="contained"
                                        onClick={updateRelatedElementFilter}
                                        disabled={inProgress} />
                            </PositiveButtons>
                            <NegativeButtons>
                                <CancelButton onClick={onClose} disabled={inProgress} />
                            </NegativeButtons>
                        </ButtonLayout>
                    </DialogActions>
                </Dialog>
            </>
    );
}